import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) { setState("error"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts > 8) { setState("error"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] grid place-items-center px-8 text-center relative z-10">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        {state === "checking" && (
          <>
            <Loader2 size={44} className="animate-spin gold-text mx-auto" />
            <div className="font-serif-display text-2xl mt-4">Confirming your payment…</div>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="gold-text mx-auto" data-testid="payment-success-icon" />
            <div className="font-serif-display text-3xl mt-4">You're all set!</div>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Your RSVP is confirmed and paid.</p>
            <button onClick={() => navigate("/reunions")} data-testid="payment-back-btn"
              className="mt-6 rounded-xl px-6 py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
              Back to Reunions
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={56} style={{ color: "var(--destructive)" }} className="mx-auto" />
            <div className="font-serif-display text-3xl mt-4">Payment not confirmed</div>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>We couldn't verify the payment. Please try again.</p>
            <button onClick={() => navigate("/reunions")}
              className="mt-6 rounded-xl px-6 py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
              Back to Reunions
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-[80vh] grid place-items-center px-8 text-center relative z-10">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <XCircle size={56} style={{ color: "var(--text-muted)" }} className="mx-auto" />
        <div className="font-serif-display text-3xl mt-4">Payment cancelled</div>
        <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>No charge was made. You can RSVP again anytime.</p>
        <button onClick={() => navigate("/reunions")} data-testid="payment-cancel-back-btn"
          className="mt-6 rounded-xl px-6 py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
          Back to Reunions
        </button>
      </motion.div>
    </div>
  );
}
